import Alert from '@material-ui/lab/Alert'
import { each, get, isEmpty, isEqual, pick } from 'lodash'
import { useCallback, useState } from 'react'
import {
  ArrayInput,
  BooleanInput,
  EditController,
  EditView,
  FormTab,
  Loading,
  maxLength,
  minLength,
  number,
  NumberInput,
  required,
  SaveButton,
  SelectInput,
  SimpleFormIterator,
  TabbedForm,
  TextInput,
  Toolbar,
  useNotify,
  useRedirect,
} from 'react-admin'
import { useFormState } from 'react-final-form'

import { partialUpdateCustomLoad } from 'api/customLoads'
import { StopDateTimeInputWindow, StopSequenceNumberWrapper } from 'components/Fields'
import {
  LoadingTypeChoices,
  STOP_TYPE_CHOICES,
  StopTypeDetailChoices,
  TIMEZONE_CHOICES,
} from 'helpers/constants'
import { getCountryFromCode } from 'helpers/countries'
import { captureExceptionIfProduction, localizeDateTimeAndStripOffset } from 'helpers/utils'
import { getStateChoices } from '../../helpers/states'
import { validateTwoDecimals } from '../../helpers/validators'
import { useHasCustomLoadsInstantBook } from '../../hooks/useHasFeatureFlag'

const SaveButtonAltered = (props: any) => {
  const { record, basePath, customLoadId, editFields, initialValues } = props
  const notify = useNotify()
  const redirectTo = useRedirect()
  const formState = useFormState()
  const [loading, setLoading] = useState(false)

  const handleClick = useCallback(
    async (values, redirect) => {
      try {
        setLoading(true)

        const customLoadValues = pick(values, editFields)
        const initialLoadValues = pick(initialValues, editFields)

        // no need to update the load if it hasn't changed
        if (!isEqual(customLoadValues, initialLoadValues)) {
          const response = await partialUpdateCustomLoad(customLoadId, {
            ...customLoadValues,
            load_stops: customLoadValues.stops,
          })

          notify('Custom Load Updated Successfully', { type: 'success' })
          redirectTo(redirect, basePath, record.id, response)
        } else {
          redirectTo(redirect, basePath, record.id)
        }

        setLoading(false)
      } catch (error: any) {
        setLoading(false)
        const data = get(error, 'response.data')
        const stringified = data ? JSON.stringify(data) : ''
        notify(`${error.message}: ${stringified}`, {
          type: 'error',
        })
        captureExceptionIfProduction(error)
      }
    },
    [notify, basePath, redirectTo, record, customLoadId, editFields, initialValues]
  )

  const loadStopErrors = formState?.errors?.load_stops
  if (loadStopErrors) {
    return <Alert severity="error">{loadStopErrors}</Alert>
  }
  if (!isEmpty(formState.errors)) {
    return <Alert severity="error">Please fix the errors above: {JSON.stringify(formState.errors)}</Alert>
  }

  return <SaveButton {...props} disabled={loading} onSave={handleClick} />
}

const CustomLoadEditToolbar = (props: any) => (
  <Toolbar {...props}>
    <SaveButtonAltered
      label="Save"
      redirect="show"
      submitOnEnter={false}
      variant="text"
      customLoadId={props.customLoadId}
      editFields={props.editFields}
      initialValues={props.initialValues}
    />
  </Toolbar>
)

const validateTripRate = [number(), validateTwoDecimals]

const CustomLoadEdit = (props: any) => {
  const hasCustomLoadsInstantBook = useHasCustomLoadsInstantBook()

  return (
    <EditController {...props}>
      {(controllerProps) => {
        const record = controllerProps.record

        const stops = record?.load_stops
        each(stops || [], (stop: any) => {
          stop.date_start = localizeDateTimeAndStripOffset(stop.date_start, stop.location_timezone)
          stop.date_end = localizeDateTimeAndStripOffset(stop.date_end, stop.location_timezone)
        })

        const initialValues = {
          company_name: record?.company_name || '',
          contact_name: record?.contact?.name || '',
          contact_phone: record?.contact?.phone || '',
          external_source_human_id: record?.external_source_human_id || '',
          show_in_search: record?.show_in_search || false,
          ghost_load: record?.ghost_load || false,
          standard_book: record?.standard_book || false,
          ...(hasCustomLoadsInstantBook && { instant_book: record?.instant_book || false }),
          trip_rate: record?.trip_rate || 0,
          stops,
        }

        const country: any = getCountryFromCode('US')
        const stateChoices = getStateChoices(country.states(true))

        return controllerProps.loading ? (
          <Loading />
        ) : (
          <EditView {...props} {...controllerProps}>
            <TabbedForm
              {...props}
              {...controllerProps}
              initialValues={initialValues}
              toolbar={
                <CustomLoadEditToolbar
                  customLoadId={record?.id}
                  editFields={Object.keys(initialValues)}
                  initialValues={initialValues}
                />
              }
            >
              <FormTab label="Custom Load Information">
                <TextInput source="company_name" label="Shipper Name" />
                <TextInput source="contact_name" label="Contact Name" />
                <TextInput source="contact_phone" label="Contact Phone" />
                <TextInput source="external_source_human_id" label="Broker load ID" />
                <BooleanInput source="show_in_search" label="Show in Search" />
                <BooleanInput source="ghost_load" label="Ghost Load" />
              </FormTab>
              <FormTab label="Trip Details">
                <BooleanInput source="standard_book" />
                {hasCustomLoadsInstantBook && <BooleanInput source="instant_book" />}
                <NumberInput source="trip_rate" validate={validateTripRate} />
              </FormTab>
              <FormTab label="Load Stops">
                <ArrayInput source="stops">
                  <SimpleFormIterator>
                    <StopSequenceNumberWrapper label="Sequence number" validate={required()} />
                    <SelectInput
                      source="type"
                      choices={STOP_TYPE_CHOICES}
                      label="Stop type"
                      validate={required()}
                    />
                    <SelectInput
                      source="type_detail"
                      label="Type detail"
                      choices={StopTypeDetailChoices}
                      validate={required()}
                    />
                    <SelectInput source="loading_type" label="Loading type" choices={LoadingTypeChoices} />
                    <StopDateTimeInputWindow />
                    <SelectInput source="location_timezone" label="Timezone" choices={TIMEZONE_CHOICES} />
                    <TextInput source="location_name" label="Facility name" />
                    <TextInput source="location_address1" label="Location address 1" />
                    <TextInput source="location_address2" label="Location address 2" />
                    <TextInput source="location_city" label="City" validate={required()} />
                    <SelectInput
                      source="location_state"
                      label="State"
                      choices={stateChoices}
                      validate={[required(), maxLength(2), minLength(2)]}
                    />
                    <TextInput source="location_zip" label="Zip" />
                    <TextInput source="notes" multiline fullWidth label="Notes" />
                    <TextInput source="reference_code" label="Reference code" validate={maxLength(250)} />
                  </SimpleFormIterator>
                </ArrayInput>
              </FormTab>
            </TabbedForm>
          </EditView>
        )
      }}
    </EditController>
  )
}

export default CustomLoadEdit
